// src/python/sigma_converter.py
var sigma_converter_default = `"""Sigma rule conversion entry points executed inside Pyodide.

This is the single source of truth for the Python that runs in the engine.
\`sigmaConverter.ts\` is generated from this file by \`scripts/generate-python.mjs\`
(run automatically before build) \u2014 do not edit the generated file by hand.
"""

from typing import List, Dict, Any, Union, Optional

import yaml

from sigma.collection import SigmaCollection
from sigma.conversion.base import Backend
from sigma.exceptions import SigmaError
from sigma.processing.pipeline import ProcessingPipeline

# Pyodide compatibility: mock MITRE ATT&CK data loading BEFORE importing plugins.
# Some backends load MITRE ATT&CK data via urllib, which doesn't work in Pyodide.
import sigma.data.mitre_attack


def _mock_load_mitre_attack_data():
    return {"techniques": {}, "tactics": {}, "groups": {}, "software": {}}


sigma.data.mitre_attack._load_mitre_attack_data = _mock_load_mitre_attack_data
sigma.data.mitre_attack._cached_data = None  # reset cache so the mock is used
# Module-level attributes some backends import directly.
sigma.data.mitre_attack.mitre_attack_tactics = {}
sigma.data.mitre_attack.mitre_attack_techniques = {}
sigma.data.mitre_attack.mitre_attack_groups = {}
sigma.data.mitre_attack.mitre_attack_software = {}

from sigma.plugins import InstalledSigmaPlugins
from sigma.processing.resolver import ProcessingPipelineResolver

# Discover plugins at module level (like sigma-cli does).
plugins = InstalledSigmaPlugins.autodiscover()
backends = plugins.backends


def get_available_pipelines(backend: str = ""):
    """Return available pipeline names, optionally filtered by backend."""
    try:
        available_pipelines = ProcessingPipelineResolver(
            InstalledSigmaPlugins.autodiscover().pipelines
        ).list_pipelines()

        if not backend:
            return [name for name, _ in available_pipelines]

        # Each entry is (name, pipeline); pipeline.allowed_backends is a frozenset.
        return [
            name
            for name, pipeline in available_pipelines
            if not pipeline.allowed_backends or backend in pipeline.allowed_backends
        ]
    except Exception as e:
        import sys
        import traceback

        print(f"Error getting pipelines: {e}")
        traceback.print_exc(file=sys.stderr)
        return []


def convert_rule(
    rule_yaml: str,
    target: str,
    pipeline_names: Optional[List[str]] = None,
    pipeline_ymls: Optional[List[str]] = None,
    filter_yml: Optional[str] = None,
    format: str = "default",
    correlation_method: Optional[str] = None,
    backend_options: Optional[Dict[str, Any]] = None,
    skip_unsupported: bool = False,
) -> Union[str, List[str], List[Dict], Dict, bytes]:
    """Convert a Sigma rule to the target format with optional pipelines.

    Args:
        rule_yaml: YAML string containing the Sigma rule.
        target: Target backend identifier.
        pipeline_names: Built-in pipeline names to resolve from plugins.
        pipeline_ymls: Custom pipeline definitions as YAML strings.
        filter_yml: Optional Sigma filter definition as YAML.
        format: Backend output format.
        correlation_method: Optional correlation method.
        backend_options: Optional backend-specific options.
        skip_unsupported: Collect (rather than raise) unsupported-rule errors.

    Returns:
        The converted rule in the backend's output format.
    """
    # Parse the rule (optionally prepending a filter document).
    if filter_yml:
        try:
            rule_yaml = filter_yml + "\\n---\\n" + rule_yaml
            rule_collection = SigmaCollection.from_yaml(rule_yaml)
        except Exception as e:
            raise SigmaError(f"Filter processing error: {str(e)}")
    else:
        rule_collection = SigmaCollection.from_yaml(rule_yaml)

    processing_pipeline = None

    # Resolve built-in pipelines by name.
    if pipeline_names:
        try:
            if isinstance(pipeline_names, str):
                pipeline_names = [pipeline_names]
            processing_pipeline = ProcessingPipelineResolver(plugins.pipelines).resolve(
                pipeline_names
            )
        except Exception as e:
            raise SigmaError(
                f"Error loading built-in pipelines {pipeline_names}: {str(e)}"
            )

    # Chain any custom YAML pipelines.
    if pipeline_ymls:
        try:
            for pipeline_yml in pipeline_ymls:
                if not pipeline_yml:
                    continue
                custom_pipeline = ProcessingPipeline.from_yaml(pipeline_yml)
                if processing_pipeline is None:
                    processing_pipeline = custom_pipeline
                else:
                    processing_pipeline = processing_pipeline + custom_pipeline
        except Exception as e:
            raise SigmaError(f"Error processing custom pipeline: {str(e)}")

    # Resolve and instantiate the backend.
    try:
        backend_class = backends[target]
    except KeyError:
        raise SigmaError(f"Backend '{target}' is not installed or does not exist.")

    try:
        backend: Backend = backend_class(
            processing_pipeline=processing_pipeline,
            collect_errors=skip_unsupported,
            **(backend_options or {}),
        )
    except TypeError as e:
        param = str(e).split("'")[1]
        raise SigmaError(f"Parameter '{param}' is not supported by backend '{target}'.")

    # Validate format and correlation method.
    if format not in backend_class.formats.keys():
        raise SigmaError(f"Output format '{format}' is not supported by backend '{target}'.")

    if correlation_method is not None:
        correlation_methods = backend.correlation_methods
        if correlation_methods is None:
            raise SigmaError(f"Backend '{target}' does not support correlations.")
        if correlation_method not in correlation_methods.keys():
            raise SigmaError(
                f"Correlation method '{correlation_method}' is not supported by backend '{target}'."
            )

    # Convert.
    result = backend.convert(rule_collection, format, correlation_method)

    # Surface collected errors unless explicitly skipping unsupported rules.
    if backend.errors and not skip_unsupported:
        error_list = [f"{str(rule.source)}: {str(error)}" for rule, error in backend.errors]
        if error_list:
            raise SigmaError("\\n".join(error_list))

    # Normalise the result to a string (or pass through structured output).
    if isinstance(result, str):
        return result
    if isinstance(result, list) and all(isinstance(item, str) for item in result):
        return "\\n\\n".join(result)
    if isinstance(result, (list, dict, bytes)):
        return result
    return str(result)
`;

// src/targets.ts
var DEFAULT_TARGETS = [
  { id: "splunk", title: "Splunk", backend: "pysigma-backend-splunk" },
  { id: "esql", title: "ES|QL", backend: "pysigma-backend-elasticsearch" },
  { id: "lucene", title: "Lucene", backend: "pysigma-backend-elasticsearch" },
  { id: "eql", title: "EQL", backend: "pysigma-backend-elasticsearch" },
  { id: "loki", title: "Loki", backend: "pysigma-backend-loki" },
  { id: "carbon_black", title: "Carbon Black", backend: "pysigma-backend-carbonblack" },
  { id: "log_scale", title: "CrowdStrike Logscale", backend: "pysigma-backend-crowdstrike" },
  { id: "datadog", title: "DataDog", backend: "pysigma-backend-datadog" },
  { id: "kusto", title: "KQL (Kusto)", backend: "pysigma-backend-kusto" },
  { id: "net_witness", title: "NetWitness", backend: "pysigma-backend-netwitness" },
  { id: "panther", title: "Panther", backend: "pysigma-backend-panther" },
  { id: "quickwit", title: "QuickWit", backend: "pysigma-backend-quickwit" },
  { id: "secops", title: "Google SecOps", backend: "pysigma-backend-secops" },
  { id: "sentinel_one", title: "Sentinel One", backend: "pysigma-backend-sentinelone" },
  { id: "sqlite", title: "SQLite", backend: "pysigma-backend-sqlite" },
  { id: "surreal_ql", title: "SurrealQL", backend: "pysigma-backend-surrealql" },
  { id: "uberagent", title: "uberAgent", backend: "pysigma-backend-uberagent" }
];
var DEFAULT_SIGMA_TARGETS = new Map(
  DEFAULT_TARGETS.map((t) => [t.id, t])
);
function createTargetRegistry(targets) {
  return new Map(targets.map((t) => [t.id, t]));
}
function backendNameFromPackage(backendPackage, fallback) {
  const match = backendPackage.match(/pysigma-backend-(.+)/);
  return match ? match[1] : fallback;
}

// src/core.ts
var SIGMA_CONVERTER_PY = sigma_converter_default;
var DEFAULT_PYSIGMA_VERSION = "1.3.2";
var DEFAULT_PIPELINE_PACKAGES = [
  "pysigma-pipeline-windows",
  "pysigma-pipeline-sysmon"
];
var IS_BROWSER = typeof window !== "undefined" || typeof importScripts !== "undefined";
var PyodideSigmaEngine = class {
  pyodide = null;
  installedBackends = /* @__PURE__ */ new Set();
  pythonModuleLoaded = false;
  sigmaNamespace = null;
  bootstrapPromise = null;
  targets;
  pysigmaVersion;
  pipelinePackages;
  indexURL;
  onStatus;
  status = {
    ready: false,
    pyodideReady: false,
    installedBackends: []
  };
  constructor(options = {}) {
    this.targets = options.targets ?? DEFAULT_SIGMA_TARGETS;
    this.pysigmaVersion = options.pysigmaVersion ?? DEFAULT_PYSIGMA_VERSION;
    this.pipelinePackages = options.pipelinePackages ?? DEFAULT_PIPELINE_PACKAGES;
    this.indexURL = options.indexURL;
    this.onStatus = options.onStatus;
  }
  /** Current engine status snapshot. */
  getStatus() {
    return { ...this.status, installedBackends: Array.from(this.installedBackends) };
  }
  /** True once Pyodide, pySigma and the Python module are loaded. */
  isReady() {
    return this.status.ready;
  }
  updateStatus(patch) {
    this.status = {
      ...this.status,
      ...patch,
      installedBackends: Array.from(this.installedBackends)
    };
    this.onStatus?.(this.status);
  }
  /**
   * Load Pyodide, install pySigma + pipelines and the Python module.
   * Safe to call multiple times — initialisation runs once.
   */
  async init() {
    if (!this.bootstrapPromise) {
      this.bootstrapPromise = this.bootstrap();
    }
    return this.bootstrapPromise;
  }
  async bootstrap() {
    try {
      this.updateStatus({ ready: false, pyodideReady: false });
      const { loadPyodide, version } = await import('pyodide');
      const indexURL = this.indexURL ?? (IS_BROWSER ? `https://cdn.jsdelivr.net/pyodide/v${version}/full/` : void 0);
      this.pyodide = await loadPyodide({
        ...indexURL ? { indexURL } : {},
        convertNullToNone: true
      });
      await this.pyodide.loadPackage(["micropip", "pyyaml"]);
      const micropip = this.pyodide.pyimport("micropip");
      await micropip.install(`pysigma==${this.pysigmaVersion}`);
      if (this.pipelinePackages.length > 0) {
        await micropip.install(this.pipelinePackages);
      }
      this.updateStatus({ pyodideReady: true });
      await this.loadPythonModule();
      this.updateStatus({ ready: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.updateStatus({ ready: false, pyodideReady: false, error: message });
      throw error;
    }
  }
  async loadPythonModule() {
    if (!this.pyodide) throw new Error("Pyodide is not initialised");
    if (!this.sigmaNamespace) {
      this.sigmaNamespace = this.pyodide.globals.get("dict")();
    }
    this.pyodide.runPython(SIGMA_CONVERTER_PY, { globals: this.sigmaNamespace });
    this.pythonModuleLoaded = true;
  }
  /**
   * Install the pySigma backend package required for the given target.
   */
  async installBackend(target) {
    await this.init();
    if (this.installedBackends.has(target)) {
      return { success: true };
    }
    const targetInfo = this.targets.get(target);
    if (!targetInfo?.backend) {
      return { success: false, error: `No backend found for target '${target}'` };
    }
    try {
      this.updateStatus({ ready: false });
      const micropip = this.pyodide.pyimport("micropip");
      await micropip.install(targetInfo.backend);
      this.installedBackends.add(target);
      await this.loadPythonModule();
      this.updateStatus({ ready: true });
      return { success: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      this.updateStatus({ error: `Error installing backend '${target}': ${message}` });
      return { success: false, error: message };
    }
  }
  /**
   * Convert a single Sigma rule. Installs the backend on demand.
   */
  async convert(params) {
    await this.init();
    const {
      rule,
      target,
      pipelines = [],
      pipelineYmls = [],
      filterYml = "",
      format = "default",
      correlationMethod = "",
      backendOptions = {}
    } = params;
    if (!this.installedBackends.has(target)) {
      const install = await this.installBackend(target);
      if (!install.success) {
        return { error: install.error, success: false };
      }
    }
    if (!this.pythonModuleLoaded) {
      await this.loadPythonModule();
    }
    try {
      const pythonParams = {
        rule,
        target,
        pipeline_names: pipelines || [],
        pipeline_ymls: pipelineYmls || [],
        filter_yml: filterYml || null,
        format,
        correlation_method: correlationMethod || null,
        backend_options: backendOptions || {}
      };
      const convertParams = this.pyodide.toPy(pythonParams);
      this.sigmaNamespace.update(convertParams);
      const result = this.pyodide.runPython(
        `convert_rule(
                    rule,
                    target,
                    pipeline_names=pipeline_names,
                    pipeline_ymls=pipeline_ymls,
                    filter_yml=filter_yml,
                    correlation_method=correlation_method,
                    backend_options=backend_options
                )`,
        { globals: this.sigmaNamespace }
      );
      return { result, success: true };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : String(error),
        success: false
      };
    }
  }
  /**
   * List pipelines available for the given target, filtered by backend
   * compatibility.
   */
  async getAvailablePipelines(target) {
    await this.init();
    if (!this.pythonModuleLoaded) {
      await this.loadPythonModule();
    }
    try {
      const targetInfo = this.targets.get(target);
      const backendName = targetInfo?.backend ? backendNameFromPackage(targetInfo.backend, target) : target;
      const pipelines = this.pyodide.runPython(
        `get_available_pipelines(${JSON.stringify(backendName)})`,
        { globals: this.sigmaNamespace }
      );
      return { success: true, pipelines: pipelines?.toJs() ?? [] };
    } catch (error) {
      return {
        success: false,
        pipelines: [],
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }
};

export { DEFAULT_PIPELINE_PACKAGES, DEFAULT_PYSIGMA_VERSION, DEFAULT_SIGMA_TARGETS, DEFAULT_TARGETS, PyodideSigmaEngine, SIGMA_CONVERTER_PY, backendNameFromPackage, createTargetRegistry };
//# sourceMappingURL=chunk-IAQJY5MS.js.map
//# sourceMappingURL=chunk-IAQJY5MS.js.map